/** The addresses that are actually SITES.
 *
 * Every import row carries an ADDRESS OF THE OPERATION — a site. The
 * contact's own address (the siège social) is a different thing and is
 * already shown under Identity as "Street address".
 *
 * Corrected 2026-09-23. EARL TELLIER occupies four rows in the source
 * workbook, one per parcel, and the UI counted three: the importer
 * consumed row 1's address as "the contact's own address" and only
 * turned rows 2..N into worksites. The importer now records every row
 * as a site, and `backfill_first_site` repairs the 327 contacts already
 * imported that way — so filtering to `worksite` here yields the true
 * count rather than hiding a real site.
 *
 * Falls back to everything when a contact has no worksite at all, so a
 * beneficiary-only contact still shows an address rather than nothing.
 */
import type { Address } from "@/lib/types";

export function worksitesOf(addresses: Address[] | undefined): Address[] {
  const all = addresses ?? [];
  const sites = all.filter((a) => a.purpose === "worksite");
  return sites.length ? sites : all;
}

/** Fold a free-text address fragment down to what actually identifies a
 *  place, so two renderings of one address compare equal.
 *
 *  Accents are stripped and every run of punctuation or whitespace
 *  becomes a single space. That is what makes the comparison work: the
 *  flat columns join with ", " while `Address.formatted` joins with a
 *  newline, so the two differed by exactly one character and a plain
 *  string compare called them different addresses.
 */
function foldAddress(value?: string | null): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, " ")
    .trim();
}

/** The flat Contact address columns, as they reach a panel. */
export type FlatAddress = {
  address_line?: string | null;
  postal_code?: string | null;
  city?: string | null;
};

/** Do the flat columns and a structured row describe the same place?
 *
 *  Reported 2026-09-27: a single-site beneficiary's address was printed
 *  twice — once as "Street address" from the flat columns, once as the
 *  site card below it. Per docs/ADDRESS_AND_CUSTOM_FIELDS.md the flat
 *  columns are a mirror of the structured `Address`, so for a
 *  beneficiary whose inspected site IS where they are based, all the
 *  copies hold one fact.
 *
 *  Compared on the parts that identify a place rather than on the
 *  formatted string. Every flat part that is PRESENT must be accounted
 *  for in the structured row; absent parts can't disagree, so they
 *  don't block a match. Country is deliberately not compared — the flat
 *  line carries it and `formatted` often doesn't, and a worksite in
 *  another country would already differ on street and postcode.
 *
 *  It stays FALSE when they genuinely differ, which is the case worth
 *  protecting: a beneficiary based in Rennes whose inspected parcel is
 *  40km away is real information, and the operator must see both.
 */
export function describesSamePlace(
  flat: FlatAddress,
  address: Address,
): boolean {
  const street = foldAddress(flat.address_line);
  const postal = foldAddress(flat.postal_code);
  const city = foldAddress(flat.city);
  // Nothing identifying on the flat side — refuse to claim a match
  // rather than hide a row on the strength of a city name alone.
  if (!street && !postal) return false;

  const formatted = foldAddress(address.formatted);
  const matches = (part: string, field?: string | null) =>
    !part || foldAddress(field) === part || formatted.includes(part);

  return (
    matches(street, null) &&
    matches(postal, address.postal_code) &&
    matches(city, address.locality)
  );
}
